import { ReactNode, useCallback, useMemo, useState } from 'react';

import { styled } from '@mui/material';
import clsx from 'clsx';
import SwipeableViewsModule, {
  SwipeableViewsProps as ReactSwipeableViewsProps,
} from 'react-swipeable-views';

import { SwipeableView } from './_components';
import { SwipeableViewContext, SwipeableViewsContext } from './context';
import { SwipeableViewsState } from './types';
import { SxProps } from '../../../types';
import { ConditionalWrapper, createSelectorSx, mergeSx } from '../../../utils';

// Vite CJS interop doesn't always unwrap .default for this package
const SwipeableViewsContainer: typeof SwipeableViewsModule =
  (SwipeableViewsModule as unknown as { default: typeof SwipeableViewsModule })
    .default ?? SwipeableViewsModule;

const StyledSwipeableViewsContainer = styled(SwipeableViewsContainer)``;

export type SlideConstructor = (
  state: SwipeableViewsState & { isActive: boolean }
) => ReactNode;

export type SwipeableViewsProps<
  C extends Record<string, ReactNode | SlideConstructor>
> = Omit<ReactSwipeableViewsProps, 'children'> & {
  slides: C;
  activeSlide?: keyof C;
  onSlideChange?: (slide: keyof C) => void;
  initialSlide?: keyof C;
  sx?: SxProps;
  slidesWrapperSx?: SxProps;
  slideWrapperSx?: SxProps;
  contentBefore?: ReactNode | ((state: SwipeableViewsState) => ReactNode);
  contentAfter?: ReactNode | ((state: SwipeableViewsState) => ReactNode);
  disableAnimations?: boolean;
  hideInactiveSlides?: boolean;
};
export const SwipeableViews = <
  C extends Record<string, ReactNode | SlideConstructor>
>({
  slides: _slides,
  activeSlide,
  sx,
  slidesWrapperSx,
  slideWrapperSx,
  initialSlide,
  onSlideChange,
  contentBefore = null,
  contentAfter = null,
  disableAnimations,
  className,
  slideClassName,
  animateHeight = true,
  hideInactiveSlides = false,
  ...props
}: SwipeableViewsProps<C>) => {
  // Filter only the slides that render something. Some steps are rendered conditionally, this way it's easier to use this component in such cases
  const slides = Object.entries(_slides).filter(([_, content]) => !!content);

  const [actionsRef, setActionsRef] = useState<{ updateHeight?: () => void }>(
    {}
  );
  const isControlled = !!activeSlide;
  const [_activeSlide, _setActiveSlide] = useState<keyof C>(() =>
    !isControlled ? initialSlide || (slides[0]?.[0] as any) : activeSlide
  );
  const currentSlide: keyof C = isControlled ? activeSlide : _activeSlide;
  const currentSlideIndex = slides.findIndex(([key]) => key === currentSlide);

  const setSlide = useCallback(
    (slide: keyof C) => {
      if (isControlled) {
        onSlideChange?.(slide);
      } else {
        _setActiveSlide(slide);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isControlled]
  );

  const prevSlideIndex = currentSlideIndex - 1;
  const nextSlideIndex = currentSlideIndex + 1;

  const state: SwipeableViewsState = useMemo(() => {
    const hasPreviousSlide = !!slides[prevSlideIndex];
    const hasNextSlide = !!slides[nextSlideIndex];
    return {
      updateHeight: actionsRef.updateHeight,
      goToFirstSlide: () => {
        if (currentSlideIndex) {
          setSlide(slides[0][0]);
        }
      },
      hasPreviousSlide,
      goToPreviousSlide: () => {
        if (hasPreviousSlide) {
          setSlide(slides[prevSlideIndex][0]);
        }
      },
      hasNextSlide,
      goToNextSlide: () => {
        if (hasNextSlide) {
          setSlide(slides[nextSlideIndex][0]);
        }
      },
      goToLastSlide: () => {
        if (currentSlideIndex !== slides.length - 1) {
          const lastSlide = slides.at(-1)?.[0];
          if (lastSlide) {
            setSlide(lastSlide);
          }
        }
      },
      setSlide,
      currentSlide: currentSlide as string,
      currentSlideIndex,
    };
  }, [
    slides,
    prevSlideIndex,
    nextSlideIndex,
    actionsRef.updateHeight,
    setSlide,
    currentSlide,
    currentSlideIndex,
  ]);

  return (
    <SwipeableViewsContext.Provider value={state}>
      {typeof contentBefore === 'function'
        ? contentBefore(state)
        : contentBefore}

      <StyledSwipeableViewsContainer
        action={setActionsRef}
        index={currentSlideIndex}
        animateHeight={animateHeight}
        sx={mergeSx(
          sx,
          slidesWrapperSx &&
            createSelectorSx(
              '& .react-swipeable-view-container',
              slidesWrapperSx
            ),
          slideWrapperSx &&
            createSelectorSx(
              '& .react-swipeable-view-container > div',
              slideWrapperSx
            )
        )}
        springConfig={
          disableAnimations
            ? {
                duration: '0s',
                easeFunction: 'cubic-bezier(0.1, 0.35, 0.2, 1)',
                delay: '0s',
              }
            : undefined
        }
        className={clsx('SwipeableViews-Root', className)}
        slideClassName={clsx('SwipeableViews-SlideContainer', slideClassName)}
        data-activeslide={activeSlide}
        {...(props as any)}
      >
        {slides.map(([key, value]) => {
          const isActive = key === currentSlide;

          /*
            Render a placeholder for slides which are not currently visible
            This maintains the correct index for each slide but prevents unwanted data fetching
          */
          if (hideInactiveSlides && !isActive) {
            return <div key={key} />;
          }

          return (
            <SwipeableViewContext.Provider key={key} value={{ key, isActive }}>
              <ConditionalWrapper
                condition={animateHeight}
                wrapper={(child) => <SwipeableView>{child}</SwipeableView>}
              >
                {typeof value === 'function'
                  ? value({ ...state, isActive })
                  : value}
              </ConditionalWrapper>
            </SwipeableViewContext.Provider>
          );
        })}
      </StyledSwipeableViewsContainer>

      {typeof contentAfter === 'function' ? contentAfter(state) : contentAfter}
    </SwipeableViewsContext.Provider>
  );
};
